import React, { useState, useEffect } from 'react';
import { FiUser, FiMail, FiLock, FiCheckCircle, FiBriefcase, FiShield } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Api from '../../Api';
import Input from '../Input';
import Select from '../Select';
import MultiSelect from '../MultiSelect';
import Button from '../Button';
import './style.css';

const MODULES = [
  { id: 'management', label: 'Gestão' },
  { id: 'companies', label: 'Empresas' },
  { id: 'financial', label: 'Financeiro' },
  { id: 'time_keeping', label: 'Controle de Ponto' },
  { id: 'patrol', label: 'Ronda de Vigilância' },
  { id: 'inspections', label: 'Inspeções' },
  { id: 'service_orders', label: 'Ordem de Serviço' },
  { id: 'sub_users', label: 'Usuários Secundários' },
];

const ACTIONS = [
  { id: 'can_view', label: 'Ver' },
  { id: 'can_create', label: 'Criar' },
  { id: 'can_edit', label: 'Editar' },
  { id: 'can_delete', label: 'Excluir' },
];

export default function SubUserForm({ subUser, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    cpf: '',
    password: '',
    status: 'active',
    access_profile: 'user',
    companies: [],
    receive_supervisor_notifications: false,
  });
  const [permissions, setPermissions] = useState([]);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Initialize permissions
    const initialPermissions = MODULES.map(m => ({
      module: m.id,
      can_view: false,
      can_create: false,
      can_edit: false,
      can_delete: false,
    }));

    if (subUser) {
      setFormData({
        name: subUser.name,
        email: subUser.email,
        cpf: subUser.cpf || '',
        password: '',
        status: subUser.status,
        access_profile: subUser.access_profile || 'user',
        companies: subUser.companies ? subUser.companies.map((c) => c.id) : [],
        receive_supervisor_notifications: subUser.receive_supervisor_notifications || false,
      });

      if (subUser.permissions && subUser.permissions.length > 0) {
        const mergedPermissions = initialPermissions.map(p => {
          const existing = subUser.permissions.find(sp => sp.module === p.module);
          return existing ? { ...p, ...existing } : p;
        });
        setPermissions(mergedPermissions);
      } else {
        setPermissions(initialPermissions);
      }
    } else {
      setPermissions(initialPermissions);
    }
    
    loadCompanies();
  }, [subUser]);

  async function loadCompanies() {
    try {
      const data = await Api.getCompanies();
      if (Array.isArray(data)) {
        setAvailableCompanies(
          data.map((c) => ({
            label: c.corporate_name || c.trade_name,
            value: c.id,
          }))
        );
      }
    } catch (e) {
      toast.error('Erro ao carregar empresas.');
    }
  }

  function handleChange(e) {
    const { id, value } = e.target;
    setFormData((prev) => ({ ...prev, [id]: value }));
  }

  function handlePermissionChange(module, action, value) {
    setPermissions(prev => prev.map(p => {
      if (p.module === module) {
        return { ...p, [action]: value };
      }
      return p;
    }));
  }

  function handleToggleAll(module, value) {
    setPermissions(prev => prev.map(p => {
      if (p.module === module) {
        return {
          ...p,
          can_view: value,
          can_create: value,
          can_edit: value,
          can_delete: value
        };
      }
      return p;
    }));
  }

  function handleGlobalToggle() {
    const allEnabled = permissions.every(p => 
      p.can_view && p.can_create && p.can_edit && p.can_delete
    );
    
    const newValue = !allEnabled;
    
    setPermissions(prev => prev.map(p => ({
      ...p,
      can_view: newValue,
      can_create: newValue,
      can_edit: newValue,
      can_delete: newValue
    })));
  }

  async function handleSubmit(e) {
    e.preventDefault();

    if (!formData.name || !formData.email) {
      toast.error('Nome e E-mail são obrigatórios.');
      return;
    }

    if (!subUser && !formData.password) {
      toast.error('Senha é obrigatória para novos usuários.');
      return;
    }

    setLoading(true);

    try {
      const payload = { ...formData, permissions };
      
      // If password is empty on edit, remove it so it doesn't get hashed as empty string
      if (subUser && !payload.password) {
        delete payload.password;
      }

      if (subUser) {
        await Api.updateSubUser(subUser.id, payload);
        toast.success('usuário atualizado com sucesso!');
      } else {
        await Api.createSubUser(payload);
        toast.success('usuário criado com sucesso!');
      }
      onSuccess();
    } catch (err) {
      console.error(err);
      toast.error(err.error || 'Erro ao salvar usuário.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} className="subuser-form-component">
      <div className="form-body">
        <h3 className="section-title">Dados do Usuário</h3>
        <div className="form-grid">
          <Input
            id="name"
            label="Nome Completo"
            placeholder="Digite o nome"
            value={formData.name}
            onChange={handleChange}
            icon={FiUser}
          />
          <Input
            id="email"
            type="email"
            label="E-mail"
            placeholder="Digite o e-mail"
            value={formData.email}
            onChange={handleChange}
            icon={FiMail}
          />
          <Input
            id="cpf"
            label="CPF"
            placeholder="000.000.000-00"
            mask="cpf"
            value={formData.cpf}
            onChange={handleChange}
            icon={FiUser}
          />
          <Input
            id="password"
            type="password"
            label={subUser ? 'Nova Senha (opcional)' : 'Senha'}
            placeholder="Digite a senha"
            value={formData.password}
            onChange={handleChange}
            icon={FiLock}
          />
          <Select
            id="status"
            label="Status"
            options={[
              { label: 'Ativo', value: 'active' },
              { label: 'Inativo', value: 'inactive' },
            ]}
            value={formData.status}
            onChange={handleChange}
            icon={FiCheckCircle}
          />
          <Select
            id="access_profile"
            label="Perfil de Acesso"
            options={[
              { label: 'Usuário', value: 'user' },
              { label: 'Administrador', value: 'admin' },
            ]}
            value={formData.access_profile}
            onChange={handleChange}
            icon={FiShield}
          />
          <div className="full-width">
            <MultiSelect
              id="companies"
              label="Empresas Permitidas"
              options={availableCompanies}
              value={formData.companies}
              onChange={(vals) =>
                setFormData((prev) => ({ ...prev, companies: vals }))
              }
              placeholder="Selecione as empresas"
              icon={FiBriefcase}
            />
          </div>
        </div>

        <>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 className="section-title" style={{ margin: 0 }}>Permissões de Acesso</h3>
            <Button
              type="button"
              onClick={handleGlobalToggle}
              variant="outline"
              style={{ padding: '0.4rem 1rem', fontSize: '0.875rem', height: 'auto' }}
            >
              {permissions.every(p => p.can_view && p.can_create && p.can_edit && p.can_delete)
                ? 'Desmarcar Todas'
                : 'Selecionar Todas'}
            </Button>
          </div>
          <div className="permissions-grid">
            <div className="permissions-header">
              <div>Módulo</div>
              {ACTIONS.map(action => (
                <div key={action.id} style={{ textAlign: 'center' }}>{action.label}</div>
              ))}
            </div>
            {permissions.map((perm) => {
              const moduleLabel = MODULES.find(m => m.id === perm.module)?.label || perm.module;
              const allSelected = perm.can_view && perm.can_create && perm.can_edit && perm.can_delete;
              
              return (
                <div key={perm.module} className="permission-row">
                  <div className="permission-module-name" onClick={() => handleToggleAll(perm.module, !allSelected)} style={{ cursor: 'pointer', fontWeight: 500 }}>
                    {moduleLabel}
                  </div>
                  {ACTIONS.map(action => (
                    <div key={action.id} className="permission-checkbox-cell">
                      <input
                        type="checkbox"
                        checked={!!perm[action.id]}
                        onChange={(e) => handlePermissionChange(perm.module, action.id, e.target.checked)}
                        className="permission-checkbox"
                      />
                    </div>
                  ))}
                </div>
              );
            })}
          </div>
        </>

        <h3 className="section-title" style={{ marginTop: '1rem' }}>Configurações</h3>
        <div 
          className={`feature-toggle-container ${formData.receive_supervisor_notifications ? 'active' : ''}`}
          onClick={() => setFormData(prev => ({ ...prev, receive_supervisor_notifications: !prev.receive_supervisor_notifications }))}
        >
          <div className="toggle-track">
            <div className="toggle-thumb" />
          </div>
          <div className="toggle-content">
            <span className="toggle-title">Receber notificações de supervisor</span>
            <span className="toggle-description">
              Habilita o recebimento de notificações relacionadas a supervisão e ocorrências.
            </span>
          </div>
        </div>
      </div>

      <div className="modal-actions">
        <Button
          type="button"
          onClick={onCancel}
          className="cancel-button"
        >
          Cancelar
        </Button>
        <Button type="submit" disabled={loading}>
          {loading ? 'Salvando...' : 'Salvar'}
        </Button>
      </div>
    </form>
  );
}
